import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PetIssue = {
  petIssueID: number;
  petIssueMainID: number;
  petIssueMainType: string;
  uniqueKey: string;
};

export interface StepType {
  name: string;
  maxSteps?: number;
}
export interface selectedPetRecommendation {
  petProfileGroupID: string;
  products?: { itemCode: string; quantity: number }[];
}

export type PetRecommendationSlice = {
  SelectedPetRecommendationGroupId: string;
  selectedFoodRecommendationList: selectedPetRecommendation[];
  selectedPrimaySolutionsRecommendationList: selectedPetRecommendation[];
  selectedSecondarySolutionsRecommendationList: selectedPetRecommendation[];
  selectedtreatsRecommendationList: selectedPetRecommendation[];
  selectedpawpairingsRecommendationList: selectedPetRecommendation[];
  activeStep: number;
  innerSteps: number[];
  steps: StepType[];
};

const loadState = (): PetRecommendationSlice | undefined => {
  if (typeof window === 'undefined') {
    // Return undefined when running on the server
    return undefined;
  }

  try {
    const serializedState = localStorage.getItem('petRecommendations');
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
    return undefined;
  }
};

const saveState = (state: PetRecommendationSlice) => {
  if (typeof window === 'undefined') {
    // Skip saving state on the server
    return;
  }

  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('petRecommendations', serializedState);
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
};

// Initial steps configuration
const initialSteps: StepType[] = [
  { name: 'Food' },
  { name: 'Solutions' },
  { name: 'Treats' },
  { name: 'PawPairings' },
  { name: 'Cart' },
];

const initialState: PetRecommendationSlice = loadState() || {
  SelectedPetRecommendationGroupId: '',
  selectedFoodRecommendationList: [],
  selectedPrimaySolutionsRecommendationList: [],
  selectedSecondarySolutionsRecommendationList: [],
  selectedtreatsRecommendationList: [],
  selectedpawpairingsRecommendationList: [],
  activeStep: 0,
  innerSteps: initialSteps.map(() => 1), // Initialize each step with inner step 1
  steps: initialSteps,
};

const petRecommendationsSlice = createSlice({
  name: 'petRecommendationsSlice',
  initialState,
  reducers: {
    updatePetRecommendation(state, { payload }: PayloadAction<Partial<PetRecommendationSlice>>) {
      Object.assign(state, { ...payload });
      saveState(state);
    },
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload;
      saveState(state);
    },
    setInnerSteps(state, action: PayloadAction<number[]>) {
      state.innerSteps = action.payload;
      saveState(state);
    },
    incrementInnerStep(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;
      const maxSteps = state.steps[stepIndex].maxSteps;
      if (maxSteps) {
        if (state.innerSteps[stepIndex] < maxSteps) {
          state.innerSteps[stepIndex] += 1;
        } else if (stepIndex === state.activeStep && state.activeStep < state.steps.length - 1) {
          state.innerSteps[state.activeStep] = 1; // Start new active step at 1
        }
      } else {
        state.activeStep += 1;
      }
      saveState(state);
    },
    decrementInnerStep(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;

      if (state.innerSteps[stepIndex] > 1) {
        state.innerSteps[stepIndex] -= 1;
      } else if (stepIndex === state.activeStep && state.activeStep > 0) {
        state.activeStep -= 1;
        const prevMaxStep = state.steps[state.activeStep].maxSteps;
        if (prevMaxStep) {
          state.innerSteps[state.activeStep] = prevMaxStep;
        }
      }
      saveState(state);
    },
    clearPetCreationState: state => {
      localStorage.removeItem('petRecommendations');
      return { ...initialState };
    },
  },
});

export const {
  clearPetCreationState,
  updatePetRecommendation,
  setActiveStep,
  setInnerSteps,
  incrementInnerStep,
  decrementInnerStep,
} = petRecommendationsSlice.actions;

export default petRecommendationsSlice;
